export const LANG = {
  en: {
    appName: "Samajh",
    appTagline: "Solving problems with guidance and care",
    heroBadge: "For Indian students · Hindi & English",
    heroTitle1: "Don't just get the answer.", heroTitle2: "Learn how to find it.",
    heroSub: "Samajh guides you step by step through any math or science problem — asking the right questions until you solve it yourself.",
    heroCta: "Start Learning →", heroNote: "No sign-up needed · Class 10 · CBSE",
    mockupProblem: "A ball is thrown horizontally from a cliff 80m high at 20 m/s. How far does it land?",
    howTitle: "How it works",
    steps: [
      { n:"01", icon:"📝", t:"Drop your problem", d:"Type it or upload a photo of your textbook, notes, or question paper." },
      { n:"02", icon:"🏫", t:"Tell us your class", d:"We tailor the guidance to exactly what you've been taught — no more, no less." },
      { n:"03", icon:"🧠", t:"Get guided, not answered", d:"Samajh asks smart questions that lead you to the solution — step by step." },
      { n:"04", icon:"📐", t:"Revise concepts mid-session", d:"Stuck on a formula? Samajh detects when you need help and offers a targeted revision." },
    ],
    featTitle: "Built for Indian students",
    feats: [
      { icon:"🇮🇳", t:"Hindi & English", d:"Full support for both languages. Switch anytime — even mid-session." },
      { icon:"📸", t:"Photo upload", d:"Snap any printed or handwritten problem. Samajh reads it instantly." },
      { icon:"🏫", t:"Class-aware guidance", d:"Tells you only what your class has covered — no overwhelming advanced theory." },
      { icon:"🤔", t:"Socratic method", d:"Used by the world's best tutors. Guides you to think — not just copy." },
      { icon:"📐", t:"Smart revision", d:"Samajh detects when you're lost and offers the right concept to revise." },
      { icon:"🔒", t:"No sign-up needed", d:"Just open the link and start. Nothing to install or register." },
    ],
    quoteTitle: "Why this approach works",
    quote: "The Socratic method — guiding through questions rather than giving answers — has been proven to produce deeper understanding and better long-term retention than passive instruction.",
    quoteSource: "Based on decades of educational research",
    ctaTitle: "Ready to actually understand?", ctaBtn: "Start Learning →",
    ctaNote: "Works on phone and desktop · No sign-up needed",
    footerTagline: "Built for students who want to actually understand — not just copy.",
    classLabel: "Which class are you in?",
    classSubtitle: "We'll tailor the guidance to what you've been taught",
    startBtn: "Let's solve this →", reviseBtn: "Revise concept", newProblem: "← Home",
    problemLabel: "Problem", photoBtn: "📸 Upload photo", orType: "or type below",
    placeholder: "Type your problem here...",
    placeholderByClass: {
      6: "e.g. Find the area of a triangle with base 8cm and height 5cm",
      7: "e.g. Solve: 3x + 7 = 22",
      8: "e.g. Find the cube root of 512",
      9: "e.g. In triangle ABC, AB=AC and angle B = 65°. Find angle A",
      10: "e.g. Find the roots of x² - 5x + 6 = 0",
    },
    tryExample: "Try an example",
    examplesByClass: {
      6: ["Find area of triangle: base 8cm, height 5cm", "A train travels 120km in 2 hours. Find its speed.", "Name the three states of matter with one example each."],
      7: ["Solve: 3x + 7 = 22", "A car travels 60km in 2 hours, stops 30 mins, then 40km in 1 hour. Find average speed.", "What happens when iron rusts? Is it physical or chemical change?"],
      8: ["Find the cube root of 512", "A rectangle has perimeter 40cm and length 12cm. Find its area.", "Differentiate between elements and compounds with examples."],
      9: ["In triangle ABC, AB=AC and angle B=65°. Find angle A.", "A body of mass 5kg is acted on by force 20N. Find acceleration.", "A solution turns blue litmus red. Is it acidic or basic?"],
      10: ["Find roots of x² - 5x + 6 = 0", "A ball is thrown horizontally at 20 m/s from 80m height. How far? (g=10)", "Balance: Fe + O₂ → Fe₂O₃"],
    },
    examples: ["Find roots of x² - 5x + 6 = 0", "Ball from 80m cliff at 20 m/s. How far? (g=10)", "Balance: Fe + O₂ → Fe₂O₃"],
    hints: ["Confused", "Simplify", "Hint please"],
    inputPlaceholder: "Your answer or thinking...", inputHint: "Enter to send · Shift+Enter for new line",
    reviseTitle: "Quick Revision", reviseSubtitle: "Pick a concept, then return to solving",
    suggestedLabel: "Suggested for your class", orTypeOwn: "Or search a concept",
    reviseInputPlaceholder: "e.g. SUVAT, Newton's laws...",
    goBtn: "Search", backBtn: "← Back", gotItBtn: "Got it — back to solving",
    reviseLoading: "Loading...", photoTitle: "Upload Problem Photo",
    photoSubtitle: "Textbook, notes, or screenshot",
    reading: "Reading image...", detected: "Problem found — edit if needed", detecting: "Reading...",
    looksGood: "Looks good — start solving", cantRead: "Couldn't read. Try clearer photo.", removePhoto: "Remove",
    needsRevision: "Looks like you might need a quick revision first.",
    reviseNow: "📖 Revise this concept",
    continueAnyway: "Continue without revising",
    classInfo: (cls, board) => `Class ${cls}${board ? ` · ${board}` : ""}`,
  },
  hi: {
    appName: "Samajh",
    appTagline: "मार्गदर्शन और देखभाल के साथ समस्याएं हल करना",
    heroBadge: "भारतीय छात्रों के लिए · हिंदी और अंग्रेजी",
    heroTitle1: "सिर्फ जवाब मत लो।", heroTitle2: "हल करना सीखो।",
    heroSub: "Samajh आपको किसी भी गणित या विज्ञान के सवाल में कदम दर कदम मार्गदर्शन देता है।",
    heroCta: "शुरू करें →", heroNote: "कोई साइन-अप नहीं · कक्षा 6–10 · CBSE और ICSE",
    mockupProblem: "80 मीटर ऊंची चट्टान से 20 m/s से क्षैतिज दिशा में गेंद फेंकी। वह कितनी दूर गिरेगी?",
    howTitle: "यह कैसे काम करता है",
    steps: [
      { n:"01", icon:"📝", t:"सवाल डालें", d:"टाइप करें या किताब, नोट्स की फोटो अपलोड करें।" },
      { n:"02", icon:"🏫", t:"कक्षा बताएं", d:"हम मार्गदर्शन आपकी पाठ्यपुस्तक के अनुसार तैयार करते हैं।" },
      { n:"03", icon:"🧠", t:"मार्गदर्शन पाएं", d:"Samajh सही सवाल पूछता है जब तक आप खुद हल न कर लें।" },
      { n:"04", icon:"📐", t:"अवधारणा दोहराएं", d:"अटक गए? Samajh सही अवधारणा का दोहराव प्रदान करता है।" },
    ],
    featTitle: "भारतीय छात्रों के लिए बना",
    feats: [
      { icon:"🇮🇳", t:"हिंदी और अंग्रेजी", d:"दोनों भाषाओं में पूरा समर्थन। कभी भी बदलें।" },
      { icon:"📸", t:"फोटो अपलोड", d:"किसी भी छपे या हस्तलिखित सवाल की फोटो लें।" },
      { icon:"🏫", t:"कक्षा-अनुसार मार्गदर्शन", d:"केवल आपकी कक्षा में पढ़ाई गई अवधारणाएं।" },
      { icon:"🤔", t:"सुकराती विधि", d:"दुनिया के सर्वश्रेष्ठ शिक्षकों की विधि।" },
      { icon:"📐", t:"स्मार्ट दोहराव", d:"जब आप अटकें, Samajh सही अवधारणा प्रदान करता है।" },
      { icon:"🔒", t:"साइन-अप नहीं", d:"बस लिंक खोलें और शुरू करें।" },
    ],
    quoteTitle: "यह तरीका क्यों काम करता है",
    quote: "सुकराती विधि — जवाब देने के बजाय सवालों से मार्गदर्शन — गहरी समझ और बेहतर याददाश्त साबित हुई है।",
    quoteSource: "दशकों के शैक्षिक शोध पर आधारित",
    ctaTitle: "सच में समझने के लिए तैयार हैं?", ctaBtn: "शुरू करें →",
    ctaNote: "फोन और डेस्कटॉप पर काम करता है · साइन-अप नहीं",
    footerTagline: "उन छात्रों के लिए जो सच में समझना चाहते हैं।",
    classLabel: "आप किस कक्षा में हैं?",
    classSubtitle: "हम मार्गदर्शन आपकी पढ़ाई के अनुसार तैयार करेंगे",
    startBtn: "हल करना शुरू करें →", reviseBtn: "अवधारणा दोहराएं", newProblem: "← होम",
    problemLabel: "सवाल", photoBtn: "📸 फोटो अपलोड करें", orType: "या नीचे लिखें",
    placeholder: "यहाँ सवाल लिखें...",
    placeholderByClass: {
      6: "जैसे: एक त्रिभुज का आधार 8 सेमी और ऊंचाई 5 सेमी है। क्षेत्रफल ज्ञात करें",
      7: "जैसे: हल करें: 3x + 7 = 22",
      8: "जैसे: 512 का घनमूल ज्ञात करें",
      9: "जैसे: त्रिभुज ABC में AB=AC और कोण B = 65°। कोण A ज्ञात करें",
      10: "जैसे: x² - 5x + 6 = 0 के मूल ज्ञात करें",
    },
    tryExample: "उदाहरण आज़माएं",
    examplesByClass: {
      6: ["त्रिभुज का क्षेत्रफल: आधार 8 सेमी, ऊंचाई 5 सेमी", "ट्रेन 2 घंटे में 120 किमी। गति क्या है?", "पदार्थ की तीन अवस्थाएं — एक-एक उदाहरण दें।"],
      7: ["हल करें: 3x + 7 = 22", "कार 2 घंटे में 60 किमी, 30 मिनट रुकी, 1 घंटे में 40 किमी। औसत गति?", "लोहे में जंग — भौतिक या रासायनिक परिवर्तन?"],
      8: ["512 का घनमूल ज्ञात करें", "आयत का परिमाप 40 सेमी, लंबाई 12 सेमी। क्षेत्रफल ज्ञात करें।", "तत्व और यौगिक में अंतर उदाहरण सहित।"],
      9: ["त्रिभुज ABC में AB=AC, कोण B=65°। कोण A ज्ञात करें।", "5kg पिंड पर 20N बल लगे। त्वरण ज्ञात करें।", "नीले लिटमस को लाल करने वाला विलयन — अम्लीय या क्षारीय?"],
      10: ["x² - 5x + 6 = 0 के मूल ज्ञात करें", "80m ऊंचाई से 20 m/s से गेंद। कितनी दूर? (g=10)", "संतुलित करें: Fe + O₂ → Fe₂O₃"],
    },
    examples: ["x² - 5x + 6 = 0 के मूल ज्ञात करें", "80m चट्टान से 20 m/s से गेंद। कितनी दूर? (g=10)", "संतुलित करें: Fe + O₂ → Fe₂O₃"],
    hints: ["उलझन है", "आसान करें", "संकेत दें"],
    inputPlaceholder: "अपना जवाब या सोच लिखें...", inputHint: "Enter = भेजें · Shift+Enter = नई लाइन",
    reviseTitle: "त्वरित दोहराव", reviseSubtitle: "अवधारणा चुनें, फिर हल पर वापस",
    suggestedLabel: "आपकी कक्षा के लिए सुझाव", orTypeOwn: "या खोजें",
    reviseInputPlaceholder: "जैसे: गति के समीकरण...",
    goBtn: "खोजें", backBtn: "← वापस", gotItBtn: "समझ गया — वापस हल करें",
    reviseLoading: "तैयार हो रहा है...", photoTitle: "फोटो अपलोड करें",
    photoSubtitle: "किताब, नोट्स या स्क्रीनशॉट",
    reading: "फोटो पढ़ी जा रही है...", detected: "सवाल मिला — बदल सकते हैं", detecting: "पढ़ा जा रहा है...",
    looksGood: "सही है — हल शुरू करें", cantRead: "फोटो साफ नहीं। दोबारा कोशिश करें।", removePhoto: "हटाएं",
    needsRevision: "लगता है पहले एक त्वरित दोहराव जरूरी है।",
    reviseNow: "📖 इस अवधारणा को दोहराएं",
    continueAnyway: "बिना दोहराव के जारी रखें",
    classInfo: (cls, board) => `कक्षा ${cls}${board ? ` · ${board}` : ""}`,
  },
};
